import { useSubscription } from '../contexts/SubscriptionContext';
import { useCatalog } from '../contexts/CatalogContext';
import { CanResult } from '../scripts/subscription.can';

export type NavAccessMode = 'normal' | 'disabled' | 'hidden';

export interface NavAccessResult {
  mode: NavAccessMode;
  reason: CanResult['reason'];
  /** The feature key that covers this ARN (for upgrade modal), null when not identifiable */
  featureKey: string | null;
  /** Whether an add-on (not a plan upgrade) could unlock this feature */
  availableAsAddon: boolean;
}

/**
 * Determines how a nav item should render based on subscription permissions.
 *
 * - role denial → hidden (the user has no business seeing this)
 * - plan/status denial → disabled (the feature exists, it can be purchased)
 * - allowed → normal
 *
 * @param action  e.g. 'read'
 * @param arn     e.g. 'digital:payroll:*'
 * @param featureKey  The vocabulary feature key covering this ARN (e.g. 'payroll').
 *                    Used to open the upgrade modal on the right feature.
 */
export function useNavAccess(
  action: string,
  arn: string,
  featureKey: string
): NavAccessResult {
  const { can: canSub } = useSubscription();
  const { addons } = useCatalog();

  const result = canSub(action, arn);

  if (result.allowed) {
    return { mode: 'normal', reason: null, featureKey, availableAsAddon: false };
  }

  if (result.reason === 'role') {
    return { mode: 'hidden', reason: 'role', featureKey, availableAsAddon: false };
  }

  // plan or status → show disabled
  // Check if an add-on could unlock this feature
  const isAddon = addons.some(a =>
    a.features && a.features[featureKey] === true
  );

  return { mode: 'disabled', reason: result.reason, featureKey, availableAsAddon: isAddon };
}
